import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, DataQualityState, EventTimeline, MapInspector, ScenarioCard } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { PageSkeleton } from '../../components/PageSkeleton';

export function WorldPage() {
  const [selectedEvent, setSelectedEvent] = useState('evt-hormuz-2026');
  const { data, isLoading, isError } = useQuery({ queryKey: ['world'], queryFn: api.worldEvents });
  const { data: playerMap } = useQuery({
    queryKey: ['player-map', selectedEvent],
    queryFn: () => api.playerMap(selectedEvent),
    enabled: !!selectedEvent,
  });
  const { data: scenarios } = useQuery({ queryKey: ['scenarios'], queryFn: api.scenarios });

  if (isLoading) return <PageSkeleton blocks={4} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="World canvas unavailable" />;

  const activeEvent = data.events.find((e) => e.eventId === selectedEvent) ?? data.events[0];

  return (
    <div className="space-y-6">
      <PageHeader title="World" subtitle="Actor map, scenarios, and transmission." />
      <ExplainActions screenName="World" />

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <AnalyticalPanel title="Geopolitical canvas" tone="feature" className="min-h-[360px]">
          <div className="world-canvas" role="img" aria-label="Accessible event list with geographic context">
            <svg viewBox="0 0 800 360" className="world-canvas__svg" aria-hidden="true">
              <rect width="800" height="360" fill="var(--color-surface-1)" />
              <path
                d="M0 280 Q200 200 400 240 T800 220"
                fill="none"
                stroke="var(--color-border-strong)"
                strokeWidth="1"
              />
              <circle cx="520" cy="180" r="6" fill="var(--color-atlas-gold)" />
              <text x="530" y="184" fill="var(--color-text-muted)" fontSize="11">
                Hormuz
              </text>
            </svg>
            <ul className="world-canvas__list">
              {data.events.map((e) => (
                <li key={e.eventId}>
                  <button
                    type="button"
                    className={selectedEvent === e.eventId ? 'is-active' : ''}
                    onClick={() => setSelectedEvent(e.eventId)}
                  >
                    <strong>{e.title}</strong>
                    <span>{e.region}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </AnalyticalPanel>
        <MapInspector
          title={activeEvent?.title ?? 'Event'}
          type={activeEvent?.classification ?? 'INFERENCE'}
          status={<span className="text-[var(--color-warning)]">{activeEvent?.status}</span>}
          facts={[
            { label: 'Region', value: activeEvent?.region ?? '—' },
            { label: 'Principals', value: String(playerMap?.principals?.length ?? '—') },
            { label: 'Known at', value: activeEvent?.knownAt?.slice(0, 10) ?? '—' },
          ]}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticalPanel title="Event timeline">
          <EventTimeline
            events={data.events.map((e) => ({
              id: e.eventId,
              time: e.occurredAt?.slice(0, 10) ?? '',
              title: e.title,
              description: e.summary,
              active: e.status === 'active',
            }))}
          />
        </AnalyticalPanel>
        <AnalyticalPanel title="Scenario paths">
          <div className="grid gap-3">
            {(scenarios?.scenarios ?? []).slice(0, 3).map((s, i) => (
              <ScenarioCard
                key={s.scenarioId}
                title={s.name}
                probability={Math.round((s.probability ?? 0.33) * 100)}
                priorProbability={Math.round((s.priorProbability ?? 0.33) * 100)}
                signal={i === 0 ? 'base' : i === 1 ? 'downside' : 'upside'}
                description={s.description}
                selected={i === 0}
              />
            ))}
          </div>
        </AnalyticalPanel>
      </div>

      {playerMap?.principals && (
        <AnalyticalPanel title="Player inspector" subtitle={activeEvent?.title}>
          <div className="grid gap-3 md:grid-cols-2">
            {playerMap.principals.map((p) => (
              <div
                key={p.playerId ?? p.name}
                className="rounded-[var(--radius-panel)] border border-[var(--color-border-default)] p-3"
              >
                <p className="font-medium text-[var(--color-text-primary)]">{p.name}</p>
                <p className="mt-1 text-xs text-[var(--color-atlas-gold)]">Cannot lose: {p.cannotLoseLine}</p>
                <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{p.rationalNextMove}</p>
              </div>
            ))}
          </div>
        </AnalyticalPanel>
      )}
    </div>
  );
}
